import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { useNavigate } from 'react-router';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import Button from 'components/Button';
import { useDebounceFn } from 'hooks/useDebounceFn';
import { useGlobalState } from 'hooks/useGlobalState';
import usePopper2 from 'hooks/usePopper2';
import { useGetPasswordPolicy, useValidatePassword } from 'hooks/useQuery';
import { requiredErrorMsg, unexpectedServerErrorMsg } from 'utils/message';
import { redirectTo } from 'utils/navigation';
import { getPopoverContainer } from 'utils/appLayout';

import styles from './OnboardingForm.module.scss';
import InputField from './InputField';
import PasswordRulesValidationList from './PasswordRulesValidationList';
import {
  startPoll,
  pollNewInstance,
  timeoutError,
  genericError,
  abortError,
  pollNewInstanceError,
} from '../util';

export const passwordNotMatchMsg = "Passwords don't match.";
// hostnamePrefix regex is from Customer Config
export const hostnamePrefixRegex = /^(?=[a-z0-9-]{2,63}$)[a-z0-9]+(-[a-z0-9]+)*$/i;
export const hostnameExistedMsg = 'That hostname is not available.';
export const hostnameMalformedMsg =
  'Invalid instance name. The name can only include numbers, letters, and hyphens.';

export default function OnboardingForm({ signupData, setApiError }) {
  const {
    clearErrors,
    register,
    handleSubmit,
    errors,
    setError,
    setValue,
    trigger,
    watch,
    formState: { isSubmitting, dirtyFields },
  } = useForm({ defaultValues: { password: '' } });
  const hostname = watch('hostname');
  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const [isPasswordFocussed, setIsPasswordFocussed] = useState(false);
  const [popupShown, setPopupShown] = useState(false);
  const [isPasswordDirty, setIsPasswordDirty] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [refEl, popperEl, arrowEl, popper] = usePopper2({
    options: {
      placement: 'right-start',
      modifiers: [
        { name: 'flip', enabled: true },
        {
          name: 'offset',
          options: { offset: [0, 18] },
        },
      ],
    },
  });

  const {
    data: passwordPolicyRules,
    isFetching: passwordPolicyRulesLoading,
  } = useGetPasswordPolicy();

  const {
    data: passwordValidationStatus,
    isFetching: passwordValidationLoading,
    refetch: revalidatePassword,
  } = useValidatePassword(password, {
    onSuccess: (data) => {
      setIsPasswordDirty(false);
      setIsPasswordValid(Object.values(data).every((valid) => !!valid));
    },
  });

  const debouncedValidatePassword = useDebounceFn(revalidatePassword, 300);
  useEffect(() => {
    if (dirtyFields.confirmPassword) {
      trigger('confirmPassword');
    }

    // If password is changed and we don't know whether the new password
    // will pass validation, set `setIsPasswordDirty(true)`. isPasswordDirty will be used in the
    // useEffect below to ensure that the password popup stays visible
    // until the password is confirmed to be valid
    setIsPasswordDirty(true);
    debouncedValidatePassword(password);
  }, [password, debouncedValidatePassword, trigger, dirtyFields]);

  useEffect(() => {
    if (isPasswordValid && !isPasswordDirty && !isPasswordFocussed) {
      setPopupShown(false);
    }

    if (dirtyFields.password) {
      trigger('password');
    }
  }, [
    isPasswordValid,
    isPasswordFocussed,
    isPasswordDirty,
    trigger,
    dirtyFields,
  ]);

  const isEveryPasswordValidAndMatching =
    isPasswordValid && password === confirmPassword;

  const isEveryFieldFilled = !!hostname && !!password && !!confirmPassword;

  const navigate = useNavigate();
  const [{ signup, waitingScreen }, dispatch] = useGlobalState();
  const { error: signupError } = signup;

  const [validating, setValidating] = useState(false);
  const canSubmit =
    !isSubmitting &&
    !validating &&
    isEmpty(errors) &&
    isEveryPasswordValidAndMatching &&
    isEveryFieldFilled;

  const clearApiError = () => setApiError(null);
  window.dataLayer = window.dataLayer || [];

  const validateHostname = (hostname) => {
    if (hostnamePrefixRegex.test(hostname)) {
      return axios
        .head(`/xm/free/api/availability/${encodeURIComponent(hostname)}`)
        .then((res) => {
          clearApiError();
          if (res.status === 204) {
            setError('hostname', {
              type: 'duplicate',
              message: hostnameExistedMsg,
            });
          }
        })
        .catch((error) => {
          const errorCode = error?.response?.status;
          if (errorCode === 400) {
            setError('hostname', {
              type: 'malformed',
              message: hostnameMalformedMsg,
            });
          } else {
            setApiError(error?.json?.message || unexpectedServerErrorMsg);
          }
        })
        .finally(() => setValidating(false));
    }
    const hostnameTooShort = hostname.length < 2;
    setError('hostname', {
      type: hostnameTooShort ? 'duplicate' : 'malformed',
      message: hostnameTooShort ? hostnameExistedMsg : hostnameMalformedMsg,
    });
    setValidating(false);
  };

  const debouncedValidateHostname = useDebounceFn(validateHostname, 500);

  useEffect(() => {
    if (hostname?.trim()?.length) {
      setValidating(true);
      clearErrors('hostname');
      debouncedValidateHostname(hostname);
    }
  }, [hostname, debouncedValidateHostname, clearErrors]);

  useEffect(() => {
    if (signupError) {
      if (signupError?.response?.status >= 500) {
        setApiError(unexpectedServerErrorMsg);
      } else
        setApiError(
          signupError?.response?.data?.message || unexpectedServerErrorMsg,
        );
    }
  }, [signupError, setApiError]);

  const postForm = (data) => axios.post(signupData.completeSignupURL, data);

  const onSubmit = (data) => {
    clearApiError();
    dispatch({
      type: 'SIGNUP_PENDING',
      payload: data.hostname,
    });
    dispatch({
      type: 'SET_WAITING_SCREEN_VISIBLE',
      payload: true,
    });
    return postForm({
      hostnamePrefix: data.hostname,
      password: data.password,
      signupStage: 'onboarding',
    })
      .then(() => {
        // Passing `dispatch()` to this util
        // function allows us to dispatch Redux
        // actions while polling
        return startPoll(
          signupData.completeSignupURL,
          signupData.region,
          dispatch,
        );
      })
      .then((redirectURL) => {
        dispatch({
          type: 'SIGNUP_COMPLETE',
          payload: redirectURL,
        });
        window.dataLayer.push({
          event: 'xm_signupComplete',
        });
        return redirectURL;
      })
      .then((redirectURL) => {
        // Passing `dispatch()` to this util
        // function allows us to dispatch Redux
        // actions while polling
        return pollNewInstance(redirectURL, dispatch);
      })
      .then((redirectURL) => {
        const sanitizedURL = encodeURI(redirectURL);
        redirectTo(sanitizedURL);
      })
      .catch((error) => {
        if (error !== abortError) {
          dispatch({
            type: 'SIGNUP_ERROR',
            payload: error,
          });
          dispatch({
            type: 'SET_WAITING_SCREEN_VISIBLE',
            payload: false,
          });
        }
        if ([timeoutError, pollNewInstanceError].includes(error)) {
          navigate('/');
          dispatch({
            type: 'SET_TIMEOUT_SCREEN_VISIBLE',
            payload: true,
          });
        }
        if (error === genericError) {
          navigate('/');
        }
      });
  };

  return (
    <form
      className={styles.onboardingForm}
      onSubmit={handleSubmit(onSubmit)}
      id="onboardingForm"
    >
      <InputField
        autoComplete="username"
        error={errors.hostname}
        fieldName="hostname"
        inputRef={register({
          required: requiredErrorMsg,
        })}
        labelText="Hostname"
        maxLength={46}
        placeholder="your-company"
        testid="onboardingForm_hostname"
        trailingText=".xmatters.com"
        validating={validating}
        onChange={(e) => setValue('hostname', e.target.value.trim())}
      />

      <PasswordRulesValidationList
        displayInline
        passwordPolicyRules={passwordPolicyRules}
        passwordPolicyRulesLoading={passwordPolicyRulesLoading}
        passwordValidationStatus={passwordValidationStatus}
        passwordValidationLoading={passwordValidationLoading || isPasswordDirty}
      />

      <div ref={refEl}>
        <InputField
          autoComplete="new-password"
          error={errors.password}
          fieldName="password"
          inputRef={register({
            required: requiredErrorMsg,
            validate: () => isPasswordValid,
          })}
          labelText="Password"
          maxLength={passwordPolicyRules?.passwordMaximumLength}
          testid="onboardingForm_password"
          type="password"
          onFocus={() => {
            setIsPasswordFocussed(true);
            setPopupShown(true);
          }}
          onChange={(e) =>
            setValue('password', e.target.value, { shouldValidate: true })
          }
          onBlur={() => setIsPasswordFocussed(false)}
        />
      </div>

      {!waitingScreen?.visible &&
        popupShown &&
        ReactDOM.createPortal(
          <div
            ref={popperEl}
            {...popper.attributes.popper}
            style={popper.styles.popper}
          >
            <PasswordRulesValidationList
              passwordPolicyRules={passwordPolicyRules}
              passwordPolicyRulesLoading={passwordPolicyRulesLoading}
              passwordValidationStatus={passwordValidationStatus}
              passwordValidationLoading={
                passwordValidationLoading || isPasswordDirty
              }
            />
            <div
              className={styles.arrow}
              ref={arrowEl}
              style={popper.styles.arrow}
            />
          </div>,
          getPopoverContainer(),
        )}

      <InputField
        autoComplete="new-password"
        error={errors.confirmPassword}
        fieldName="confirmPassword"
        inputRef={register({
          required: requiredErrorMsg,
          validate: (value) => value === password || passwordNotMatchMsg,
        })}
        labelText="Verify Password"
        maxLength={100}
        testid="onboardingForm_confirmPassword"
        type="password"
        onChange={(e) =>
          setValue('confirmPassword', e.target.value, { shouldValidate: true })
        }
      />

      <div className={styles.btnWrap}>
        <Button
          className={styles.button}
          type="submit"
          color="primary"
          disabled={!canSubmit}
          data-testid="onboardingForm_btn"
        >
          {isSubmitting ? 'Please wait' : 'Create Account'}
        </Button>
      </div>
    </form>
  );
}

OnboardingForm.propTypes = {
  signupData: PropTypes.shape({
    completeSignupURL: PropTypes.string.isRequired,
    region: PropTypes.string,
  }),
  setApiError: PropTypes.func.isRequired,
};

OnboardingForm.defaultProps = {
  signupData: null,
};
